import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { DollarSign, Clock, SquareIcon } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { formatCurrency } from "@/lib/utils";

interface AddFloorcareServiceSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd?: (service: any) => void;
}

export function AddFloorcareServiceSheet({
  isOpen,
  onClose,
  onAdd = () => {},
}: AddFloorcareServiceSheetProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pricingMethod, setPricingMethod] = useState<"hourly" | "squareFootage">("hourly");
  const [isCalculationApplied, setIsCalculationApplied] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [serviceData, setServiceData] = useState<{
    name: string;
    description: string;
    frequency: string;
    serviceDays: string[];
    hourlyRate: number;
    estimatedHours: number;
    squareFootage: number;
    ratePerSqFt: number;
    productionRate: number;
    overheadPercentage: number;
    profitMargin: number;
  }>({
    name: "",
    description: "",
    frequency: "5_per_week",
    serviceDays: [],
    // Hourly method fields
    hourlyRate: 0,
    estimatedHours: 0,
    // Square footage method fields
    squareFootage: 0,
    ratePerSqFt: 0,
    productionRate: 0,
    // Common fields
    overheadPercentage: 0,
    profitMargin: 0,
  });
  const { toast } = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const newValue = ["name", "description"].includes(id) ? value : parseFloat(value) || 0;

    // Log for debugging
    console.log(
      `AddFloorcareServiceSheet - handleInputChange - Field: ${id}, Value: ${value}, Parsed Value: ${newValue}, Type: ${typeof newValue}`
    );

    if (id === "ratePerSqFt") {
      console.log(
        `AddFloorcareServiceSheet - Setting ratePerSqFt to ${newValue} (${typeof newValue})`
      );
    }

    const updatedData = {
      ...serviceData,
      [id]: newValue,
    };

    // Calculate estimated hours if both square footage and production rate are > 0
    if (
      (id === "squareFootage" || id === "productionRate") &&
      updatedData.squareFootage > 0 &&
      updatedData.productionRate > 0
    ) {
      // Calculate and update the estimated hours
      const calculatedHours = updatedData.squareFootage / updatedData.productionRate;
      updatedData.estimatedHours = parseFloat(calculatedHours.toFixed(2));

      // Reset the applied state when square footage or production rate changes
      setIsCalculationApplied(false);

      // Log the calculation for debugging
      console.log(
        `Calculated hours: ${calculatedHours.toFixed(2)} from ${updatedData.squareFootage} sq ft at ${updatedData.productionRate} sq ft/hr`
      );
    }

    // Update the service data
    setServiceData(updatedData);
  };

  const handleSelectChange = (id: string, value: string) => {
    const updatedData = {
      ...serviceData,
      [id]: value,
    };

    // Update the service data
    setServiceData(updatedData);
  };

  // Helper function to format frequency for display
  const formatFrequencyForDisplay = (frequency: string) => {
    if (frequency.includes("_per_week")) {
      const timesPerWeek = parseInt(frequency.split("_")[0]);
      return timesPerWeek === 1 ? "Weekly (1 time per week)" : `${timesPerWeek} Days per Week`;
    } else if (frequency.includes("_per_month")) {
      const timesPerMonth = parseInt(frequency.split("_")[0]);
      return timesPerMonth === 1 ? "Monthly" : `${timesPerMonth} Times per Month`;
    } else if (frequency === "quarterly") {
      return "Quarterly";
    } else if (frequency === "semiannually") {
      return "Semi-Annually";
    } else if (frequency === "annually") {
      return "Annually";
    }
    return frequency;
  };

  const handlePricingMethodChange = (method: "hourly" | "squareFootage") => {
    setPricingMethod(method);

    // Set frequency to quarterly when switching to square footage method
    if (method === "squareFootage") {
      const updatedData = {
        ...serviceData,
        frequency: "quarterly",
      };

      setServiceData(updatedData);
    }
  };

  // Calculate pricing based on method
  const calculateHourlyPrice = (data = serviceData) => {
    const { hourlyRate, estimatedHours, frequency, overheadPercentage, profitMargin } = data;

    // Calculate base cost for a single service
    const singleServiceCost = hourlyRate * estimatedHours;

    // Calculate services per month based on frequency
    let servicesPerMonth = 0;

    if (frequency.includes("per_week")) {
      const timesPerWeek = parseInt(frequency.split("_")[0]);
      servicesPerMonth = timesPerWeek * 4.33; // Average weeks per month
    } else if (frequency.includes("per_month")) {
      const timesPerMonth = parseInt(frequency.split("_")[0]);
      servicesPerMonth = timesPerMonth;
    }

    // Calculate monthly base price
    let monthlyPrice = singleServiceCost * servicesPerMonth;

    // Apply overhead if provided
    if (overheadPercentage > 0) {
      const overheadAmount = monthlyPrice * (overheadPercentage / 100);
      monthlyPrice += overheadAmount;
    }

    // Apply profit margin if provided
    if (profitMargin > 0) {
      const profitAmount = monthlyPrice * (profitMargin / 100);
      monthlyPrice += profitAmount;
    }

    return monthlyPrice;
  };

  const calculateSquareFootagePrice = (data = serviceData) => {
    const { squareFootage, ratePerSqFt, overheadPercentage, profitMargin, frequency } = data;

    // Calculate base cost for a single service
    const singleServiceCost = squareFootage * ratePerSqFt;

    // Add overhead
    const overhead = singleServiceCost * (overheadPercentage / 100);
    const subtotalWithOverhead = singleServiceCost + overhead;

    // Add profit margin
    const profit = subtotalWithOverhead * (profitMargin / 100);
    const totalSingleServiceCost = subtotalWithOverhead + profit;

    // Calculate monthly price based on frequency
    let servicesPerMonth = 0;

    // Handle both old and new frequency formats consistently
    if (frequency.includes("per_week")) {
      const timesPerWeek = parseInt(frequency.split("_")[0]);
      servicesPerMonth = timesPerWeek * 4.33; // Average weeks per month
    } else if (frequency.includes("per_month")) {
      const timesPerMonth = parseInt(frequency.split("_")[0]);
      servicesPerMonth = timesPerMonth;
    } else {
      // Handle special frequency formats
      switch (frequency) {
        case "quarterly":
          servicesPerMonth = 4 / 12; // 4 services per year / 12 months
          break;
        case "semiannually":
          servicesPerMonth = 2 / 12; // 2 services per year / 12 months
          break;
        case "annually":
          servicesPerMonth = 1 / 12; // 1 service per year / 12 months
          break;
        default:
          servicesPerMonth = 1; // Default to monthly if unknown
      }
    }

    // Calculate monthly price by multiplying single service cost by services per month
    const monthlyPrice = totalSingleServiceCost * servicesPerMonth;

    return monthlyPrice;
  };

  // Update the total price whenever the component renders
  useEffect(() => {
    const calculatedPrice =
      pricingMethod === "hourly" ? calculateHourlyPrice() : calculateSquareFootagePrice();

    setTotalPrice(calculatedPrice);
  }, [serviceData, pricingMethod]);

  const validateForm = () => {
    console.log("Validating form with data:", serviceData);
    console.log("Current pricing method:", pricingMethod);

    if (!serviceData.name) {
      console.log("Validation failed: No service name");
      toast({
        title: "Error",
        description: "Please select a service",
        variant: "destructive",
      });
      return false;
    }

    if (serviceData.name === "custom" && !serviceData.description) {
      console.log("Validation failed: Custom service with no description");
      toast({
        title: "Error",
        description: "Please enter a description for the custom service",
        variant: "destructive",
      });
      return false;
    }

    if (pricingMethod === "hourly") {
      if (!serviceData.estimatedHours) {
        console.log("Validation failed: No estimated hours for hourly pricing");
        toast({
          title: "Error",
          description: "Please enter estimated hours",
          variant: "destructive",
        });
        return false;
      }
      if (!serviceData.hourlyRate) {
        console.log("Validation failed: No hourly rate for hourly pricing");
        toast({
          title: "Error",
          description: "Please enter hourly rate",
          variant: "destructive",
        });
        return false;
      }
    }

    if (pricingMethod === "squareFootage") {
      if (!serviceData.squareFootage) {
        console.log("Validation failed: No square footage for square footage pricing");
        toast({
          title: "Error",
          description: "Please enter square footage",
          variant: "destructive",
        });
        return false;
      }
      if (serviceData.ratePerSqFt === undefined || serviceData.ratePerSqFt === null) {
        console.log("Validation failed: No rate per sq ft for square footage pricing");
        toast({
          title: "Error",
          description: "Please enter rate per square foot",
          variant: "destructive",
        });
        return false;
      }
    }

    // Only require service days for hourly pricing method
    if (pricingMethod === "hourly" && !serviceData.serviceDays.length) {
      console.log("Validation failed: No service days selected");
      toast({
        title: "Error",
        description: "Please select at least one service day",
        variant: "destructive",
      });
      return false;
    }

    // Ensure overhead and profit margin are defined (required by schema)
    if (serviceData.overheadPercentage === undefined || serviceData.overheadPercentage === null) {
      console.log("Setting default overhead percentage to 0");
      setServiceData((prev) => ({ ...prev, overheadPercentage: 0 }));
    }

    if (serviceData.profitMargin === undefined || serviceData.profitMargin === null) {
      console.log("Setting default profit margin to 0");
      setServiceData((prev) => ({ ...prev, profitMargin: 0 }));
    }

    console.log("Form validation passed");
    return true;
  };

  const handleSubmit = async () => {
    console.log("Submit button clicked");

    if (!validateForm()) {
      console.log("Form validation failed, not submitting");
      return;
    }

    try {
      console.log("Setting isSubmitting to true");
      setIsSubmitting(true);

      // Create a complete service payload with all required fields
      const servicePayload = {
        name: serviceData.name,
        description: serviceData.description,
        frequency: serviceData.frequency,
        pricingMethod,
        totalPrice,
        type: "floorcare",
        // Set hourly values to 0 when using square footage method
        hourlyRate: pricingMethod === "squareFootage" ? 0 : serviceData.hourlyRate || 0,
        estimatedHours: pricingMethod === "squareFootage" ? 0 : serviceData.estimatedHours || 0,
        // Always include square footage fields
        squareFootage: serviceData.squareFootage || 0,
        ratePerSqFt: serviceData.ratePerSqFt || 0,
        productionRate: serviceData.productionRate || 0,
        // Ensure overhead and profit margin are included (required by schema)
        overheadPercentage: serviceData.overheadPercentage || 0,
        profitMargin: serviceData.profitMargin || 0,
        // Ensure service days are included - for square footage method, use default weekdays
        serviceDays:
          pricingMethod === "hourly" && serviceData.serviceDays.length > 0
            ? serviceData.serviceDays
            : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      };

      console.log("Submitting floorcare service:", JSON.stringify(servicePayload, null, 2));
      console.log("ratePerSqFt in payload:", servicePayload.ratePerSqFt);
      console.log("ratePerSqFt type:", typeof servicePayload.ratePerSqFt);

      // Log hourly values to verify they're set to 0 when using square footage method
      console.log("Pricing method:", pricingMethod);
      console.log("Hourly rate in payload:", servicePayload.hourlyRate);
      console.log("Estimated hours in payload:", servicePayload.estimatedHours);

      console.log("onAdd function type:", typeof onAdd);

      console.log("Calling onAdd function");
      await onAdd(servicePayload);
      console.log("onAdd function completed successfully");

      toast({
        title: "Success!",
        description: "Floorcare service added successfully",
      });
      onClose();
    } catch (error) {
      console.log("Error in handleSubmit:", error);
      toast({
        title: "Error",
        description: "Failed to add floorcare service",
        variant: "destructive",
      });
      console.error(error);
    } finally {
      console.log("Setting isSubmitting to false");
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Add Floorcare Service</SheetTitle>
          <SheetDescription>Create a new floorcare service with pricing details.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">Service</Label>
              <Select
                value={serviceData.name}
                onValueChange={(value) => handleSelectChange("name", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Carpet Cleaning">Carpet Cleaning</SelectItem>
                  <SelectItem value="Floor Stripping and Waxing">
                    Floor Stripping and Waxing
                  </SelectItem>
                  <SelectItem value="Tile & Grout Cleaning">Tile & Grout Cleaning</SelectItem>
                  <SelectItem value="Concrete Polishing">Concrete Polishing</SelectItem>
                  <SelectItem value="Hardwood Floor Refinishing">
                    Hardwood Floor Refinishing
                  </SelectItem>
                  <SelectItem value="Marble Restoration">Marble Restoration</SelectItem>
                  <SelectItem value="Stone Floor Cleaning">Stone Floor Cleaning</SelectItem>
                  <SelectItem value="Vinyl Floor Maintenance">Vinyl Floor Maintenance</SelectItem>
                  <SelectItem value="Terrazzo Restoration">Terrazzo Restoration</SelectItem>
                  <SelectItem value="custom">Add Custom Service</SelectItem>
                </SelectContent>
              </Select>
              {serviceData.name === "custom" && (
                <Input
                  id="name"
                  className="mt-2"
                  placeholder="Enter custom service name"
                  value={serviceData.name === "custom" ? "" : serviceData.name}
                  onChange={handleInputChange}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Service Description</Label>
              <Input
                id="description"
                placeholder="Brief description of the service"
                value={serviceData.description}
                onChange={handleInputChange}
              />
            </div>

            <Separator className="my-4" />

            <div className="space-y-4">
              <Label>Pricing Method</Label>
              <RadioGroup
                value={pricingMethod}
                onValueChange={handlePricingMethodChange}
                className="flex space-x-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="hourly"
                    id="hourly"
                    className="border-blue-600 text-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  />
                  <Label
                    htmlFor="hourly"
                    className={`${pricingMethod === "hourly" ? "text-gray-900" : "text-gray-400"}`}
                  >
                    Hourly Rate
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="squareFootage"
                    id="squareFootage"
                    className="border-blue-600 text-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  />
                  <Label
                    htmlFor="squareFootage"
                    className={`${pricingMethod === "squareFootage" ? "text-gray-900" : "text-gray-400"}`}
                  >
                    Square Footage
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Tabs value={pricingMethod} className="mt-6">
              <TabsContent value="hourly" className="space-y-4">
                <div className="mb-3 text-sm text-gray-600">
                  Hourly method works well for specialized floorcare services.
                </div>

                {/* Optional Square Footage and Production Rate fields */}
                <div className="mb-4 rounded-md bg-gray-50 p-3">
                  <h4 className="mb-2 text-sm font-medium">
                    Optional: Calculate Hours from Square Footage
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="squareFootage">Floor Area (sq ft)</Label>
                        <div className="group relative">
                          <span className="cursor-help text-gray-500 hover:text-gray-700">?</span>
                          <div className="invisible absolute right-0 z-10 mt-1 w-64 rounded border bg-white p-2 text-xs shadow-lg transition-opacity group-hover:visible">
                            Total floor area that requires the selected floorcare service
                          </div>
                        </div>
                      </div>
                      <Input
                        id="squareFootage"
                        type="number"
                        placeholder="0"
                        value={serviceData.squareFootage}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="productionRate">Production Rate (sq ft/hr)</Label>
                        <div className="group relative">
                          <span className="cursor-help text-gray-500 hover:text-gray-700">?</span>
                          <div className="invisible absolute right-0 z-10 mt-1 w-64 rounded border bg-white p-2 text-xs shadow-lg transition-opacity group-hover:visible">
                            <div className="mb-1 font-semibold">Average Production Rates</div>
                            Carpet cleaning: 500-800 sq ft/hr
                            <br />
                            Strip and wax: 300-500 sq ft/hr
                            <br />
                            Tile & grout: 200-400 sq ft/hr
                          </div>
                        </div>
                      </div>
                      <Input
                        id="productionRate"
                        type="number"
                        placeholder="0"
                        value={serviceData.productionRate}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  {serviceData.squareFootage > 0 && serviceData.productionRate > 0 && (
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-sm text-blue-600">
                        Estimated hours:{" "}
                        {(serviceData.squareFootage / serviceData.productionRate).toFixed(2)} hours
                      </p>
                      <Button
                        type="button"
                        variant={isCalculationApplied ? "default" : "outline"}
                        size="sm"
                        className={
                          isCalculationApplied ? "bg-green-600 text-white hover:bg-green-700" : ""
                        }
                        onClick={() => {
                          const calculatedHours =
                            serviceData.squareFootage / serviceData.productionRate;
                          setServiceData((prev) => ({
                            ...prev,
                            estimatedHours: parseFloat(calculatedHours.toFixed(2)),
                            // Ensure squareFootage and productionRate are explicitly saved in state
                            squareFootage: prev.squareFootage,
                            productionRate: prev.productionRate,
                          }));

                          // Set the applied state to true
                          setIsCalculationApplied(true);

                          // Log the updated state for debugging
                          console.log("Applied square footage calculation:", {
                            squareFootage: serviceData.squareFootage,
                            productionRate: serviceData.productionRate,
                            calculatedHours: parseFloat(calculatedHours.toFixed(2)),
                          });
                        }}
                      >
                        {isCalculationApplied ? (
                          <span className="flex items-center">
                            <svg
                              className="mr-1 h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Applied
                          </span>
                        ) : (
                          "Apply"
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Hourly Rate and Estimated Hours fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      placeholder="0.00"
                      value={serviceData.hourlyRate}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="estimatedHours">Estimated Hours</Label>
                    <div className="relative">
                      <Input
                        id="estimatedHours"
                        type="number"
                        placeholder="0"
                        value={serviceData.estimatedHours}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Reset the applied state when manually changing the estimated hours
                          setIsCalculationApplied(false);
                        }}
                        className={
                          isCalculationApplied ? "border-green-500 focus:ring-green-500" : ""
                        }
                      />
                      {isCalculationApplied && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 transform text-green-600">
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label htmlFor="frequency">Service Frequency</Label>
                  <Select
                    value={serviceData.frequency}
                    onValueChange={(value) => handleSelectChange("frequency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6_per_week">6 Days per Week</SelectItem>
                      <SelectItem value="5_per_week">5 Days per Week</SelectItem>
                      <SelectItem value="4_per_week">4 Days per Week</SelectItem>
                      <SelectItem value="3_per_week">3 Days per Week</SelectItem>
                      <SelectItem value="2_per_week">2 Days per Week</SelectItem>
                      <SelectItem value="1_per_week">1 Day per Week</SelectItem>
                      <SelectItem value="2_per_month">Bi-Monthly</SelectItem>
                      <SelectItem value="1_per_month">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-4 space-y-2">
                  <Label htmlFor="serviceDays">Preferred Service Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                      "Sunday",
                    ].map((day) => (
                      <button
                        key={day}
                        type="button"
                        className={`rounded-full border px-3 py-1.5 text-sm transition-all ${
                          serviceData.serviceDays?.includes(day)
                            ? "border-blue-500 bg-blue-500 text-white"
                            : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                        }`}
                        onClick={() => {
                          const currentDays = serviceData.serviceDays || [];
                          const newDays = currentDays.includes(day)
                            ? currentDays.filter((d) => d !== day)
                            : [...currentDays, day];
                          setServiceData({
                            ...serviceData,
                            serviceDays: newDays,
                          });
                        }}
                      >
                        {day.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="squareFootage" className="space-y-4">
                <div className="mb-3 text-sm text-gray-600">
                  Square footage method works well for standardized floorcare services.
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="squareFootage">Square Footage</Label>
                    <Input
                      id="squareFootage"
                      type="number"
                      placeholder="0"
                      value={serviceData.squareFootage}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ratePerSqFt">Rate per Sq Ft ($)</Label>
                    <Input
                      id="ratePerSqFt"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={serviceData.ratePerSqFt}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label htmlFor="frequency">Service Frequency</Label>
                  <Select
                    value={serviceData.frequency}
                    onValueChange={(value) => handleSelectChange("frequency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="1_per_week">Weekly (1 time per week)</SelectItem>
                      <SelectItem value="1_per_month">Monthly</SelectItem>
                      <SelectItem value="semiannually">Semi-Annually</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>

            {/* Add overhead and profit margin fields */}
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="overheadPercentage">Overhead (%)</Label>
                <Input
                  id="overheadPercentage"
                  type="number"
                  value={serviceData.overheadPercentage}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setServiceData((prev) => ({
                      ...prev,
                      overheadPercentage: value,
                    }));
                  }}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="profitMargin">Profit Margin (%)</Label>
                <Input
                  id="profitMargin"
                  type="number"
                  value={serviceData.profitMargin}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setServiceData((prev) => ({
                      ...prev,
                      profitMargin: value,
                    }));
                  }}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="mt-6 rounded-md bg-blue-50 p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Monthly Price:</span>
                <span className="flex items-center text-xl font-bold text-blue-700">
                  <DollarSign className="h-5 w-5" />
                  {formatCurrency(totalPrice).replace("$", "")}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {pricingMethod === "hourly"
                  ? `Based on ${serviceData.estimatedHours} hours at $${serviceData.hourlyRate}/hour (${formatFrequencyForDisplay(serviceData.frequency)})`
                  : `Based on ${serviceData.squareFootage} sq ft at $${serviceData.ratePerSqFt}/sq ft (${formatFrequencyForDisplay(serviceData.frequency)})`}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {serviceData.overheadPercentage > 0 || serviceData.profitMargin > 0 ? (
                  <>
                    Includes
                    {serviceData.overheadPercentage > 0 &&
                      ` ${serviceData.overheadPercentage}% overhead`}
                    {serviceData.overheadPercentage > 0 && serviceData.profitMargin > 0 && " and"}
                    {serviceData.profitMargin > 0 && ` ${serviceData.profitMargin}% profit margin`}
                  </>
                ) : (
                  "No overhead or profit margin included"
                )}
              </p>
            </div>

            <Button
              className="mt-6 w-full"
              onClick={(e) => {
                console.log("Button clicked, event:", e);
                handleSubmit();
              }}
              disabled={isSubmitting}
              type="button"
            >
              {isSubmitting ? "Adding..." : "Add Floorcare Service"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
